import React from 'react'
import {
	CircleArrowUpIcon,
	LayersIcon,
	LoaderCircleIcon,
	MoreVerticalIcon,
	PencilIcon,
	RotateCcwIcon,
	Trash2Icon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApplyScenario } from '@/features/scenario/apply'
import { DeleteScenario } from '@/features/scenario/delete'
import { DuplicateScenario } from '@/features/scenario/duplicate'
import { RenameScenario } from '@/features/scenario/rename'

import { FormProvider } from '@/shared/components/HookForm'
import { Button } from '@/shared/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import {
	ModalBody,
	ModalClose,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { Separator } from '@/shared/components/ui/separator'
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '@/shared/components/ui/tabs'
import { ampli } from '@/shared/lib/ampli'

import { ScenarioItemBusinessConstraints } from './ScenarioItemBusinessConstraints'
import { useScenarioItemContext } from './ScenarioItemProvider'
import { ScenarioItemStopList } from './ScenarioItemStopList'
import { ScenarioItemStrategy } from './ScenarioItemStrategy'
import { ScenarioModalDescription } from './ScenarioModalDescription'

export default function ScenarioModalForm() {
	const { t } = useTranslation()

	const context = useScenarioItemContext()

	return (
		<FormProvider
			methods={context.methods}
			onSubmit={context.handleSubmit}
			className='flex h-full flex-col overflow-hidden p-4'
		>
			<ModalHeader className='pr-6'>
				<div className='flex w-full items-start justify-between'>
					<ModalTitle>
						{t('scenario.edit.modal.title', { defaultValue: 'Scenario' })}
						{': '}
						{context.methods.watch('name') || 'None'}
					</ModalTitle>
					<div className='flex items-center gap-2'>
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant='ghost' size='icon-sm'>
									<MoreVerticalIcon />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent side='bottom' align='end'>
								<ApplyScenario scenario={context.scenario!}>
									<DropdownMenuItem>
										<CircleArrowUpIcon />
										{t('scenario.apply', {
											defaultValue: 'Promote to the main',
										})}
									</DropdownMenuItem>
								</ApplyScenario>
								<DropdownMenuSeparator />
								<RenameScenario scenarioId={context.scenario!.id}>
									<DropdownMenuItem>
										<PencilIcon />{' '}
										{t('app.edit_name', { defaultValue: 'Edit name' })}
									</DropdownMenuItem>
								</RenameScenario>
								<DuplicateScenario scenarioId={context.scenario!.id}>
									<DropdownMenuItem>
										<LayersIcon />
										{t('scenario.duplicate', {
											defaultValue: 'Duplicate scenario',
										})}
									</DropdownMenuItem>
								</DuplicateScenario>
								<DeleteScenario scenarioId={context.scenario!.id}>
									<DropdownMenuItem
										disabled={context.scenario!.is_main}
										variant='destructive'
									>
										<Trash2Icon />
										{t('scenario.delete', { defaultValue: 'Delete scenario' })}
									</DropdownMenuItem>
								</DeleteScenario>
							</DropdownMenuContent>
						</DropdownMenu>

						<Separator orientation='vertical' className='mr-2 h-6!' />
					</div>
				</div>
			</ModalHeader>
			<ScenarioModalDescription />
			<Tabs
				defaultValue='strategy'
				asChild
				onValueChange={(value) => {
					ampli.scenarioTabClick({
						tab: value,
					})
				}}
			>
				<ModalBody className='overflow-hidden'>
					<TabsList>
						<TabsTrigger
							data-testid='scenario-modal-tab'
							data-tab='straregy'
							key='strategy'
							value='strategy'
						>
							{t('settings.strategy.short', { defaultValue: 'Strategy' })}
						</TabsTrigger>
						<TabsTrigger
							data-testid='scenario-modal-tab'
							data-tab='business'
							key='business'
							value='business'
						>
							{t('settings.business.short', { defaultValue: 'Constrains' })}
						</TabsTrigger>
						<TabsTrigger
							data-testid='scenario-modal-tab'
							data-tab='stop_list'
							key='stop_list'
							value='stop_list'
						>
							{t('settings.stop_list.short', { defaultValue: 'Stop list' })}
						</TabsTrigger>
					</TabsList>
					<TabsContent
						data-testid='scenario-modal-tab-content'
						className='-mx-4 flex flex-col overflow-y-auto px-4'
						value='strategy'
						key='strategy'
					>
						<ScenarioItemStrategy prefix='settings' />
					</TabsContent>
					<TabsContent
						data-testid='scenario-modal-tab-content'
						className='-mx-4 flex flex-col overflow-y-auto px-4'
						value='business'
						key='business'
					>
						<ScenarioItemBusinessConstraints prefix='settings' />
					</TabsContent>
					<TabsContent
						data-testid='scenario-modal-tab-content'
						className='-mx-4 flex flex-col overflow-y-auto px-4'
						value='stop_list'
						key='stop_list'
					>
						<ScenarioItemStopList
							prefix='settings'
							skuRules={context.skuRules}
						/>
					</TabsContent>
				</ModalBody>
			</Tabs>
			<ModalFooter>
				<ModalClose
					data-testid='scenario-form-cancel'
					asChild
					className='mr-auto'
				>
					<Button variant='outline'>{t('general_cancel')}</Button>
				</ModalClose>
				<Button
					data-testid='scenario-form-revert-changes'
					type='reset'
					disabled={!context.methods.formState.isDirty}
					variant='outline'
					onClick={context.handleReset}
				>
					<RotateCcwIcon />
					{t('app.revert_changes')}
				</Button>
				<Button
					data-testid='scenario-form-save'
					disabled={context.isPending || !context.methods.formState.isDirty}
					type='submit'
					variant='primary'
				>
					{context.isPending && <LoaderCircleIcon className='animate-spin' />}
					{t('general_save')}
				</Button>
			</ModalFooter>
		</FormProvider>
	)
}
